import React from 'react';

// Los mismos colores que se pintan en la celda de la planilla, para que el
// admin vea acá el código que después va a leer en el Excel.
const COLOR_CHOICES = [
  { value: '', label: 'Sin color' },
  { value: '#fce5cd', label: 'Naranja claro' },
  { value: '#f79902', label: 'Naranja fuerte' },
];

const CommentOptionsEditor = ({ options, onChange }) => {
  const update = (index, changes) => {
    onChange(options.map((o, i) => (i === index ? { ...o, ...changes } : o)));
  };

  const remove = (index) => {
    onChange(options.filter((_, i) => i !== index));
  };

  const add = () => {
    onChange([...options, { label: '', requires_detail: false, sheet_color: null }]);
  };

  return (
    <>
      {options.length === 0 && (
        <p style={{ color: '#888', fontSize: '0.88rem' }}>
          Sin opciones: la app no le va a pedir ningún comentario al socio.
        </p>
      )}

      {options.map((option, index) => (
        <div
          key={index}
          className="row align-items-center"
          style={{
            marginBottom: '8px',
            padding: '8px 0',
            borderBottom: index < options.length - 1 ? '1px solid #eee' : 'none',
          }}
        >
          <div className="col-md-5 mb-2 mb-md-0">
            <input
              type="text"
              className="form-control"
              placeholder="Ej: Ida y vuelta"
              maxLength={100}
              value={option.label}
              onChange={(e) => update(index, { label: e.target.value })}
            />
          </div>

          <div className="col-md-3 mb-2 mb-md-0">
            <select
              className="form-select"
              value={option.sheet_color || ''}
              onChange={(e) => update(index, { sheet_color: e.target.value || null })}
              style={{ backgroundColor: option.sheet_color || undefined }}
            >
              {COLOR_CHOICES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className="col-md-3 mb-2 mb-md-0">
            <div className="form-check" style={{ marginBottom: 0 }}>
              <input
                type="checkbox"
                className="form-check-input"
                id={`requires_detail_${index}`}
                checked={!!option.requires_detail}
                onChange={(e) => update(index, { requires_detail: e.target.checked })}
              />
              <label
                className="form-check-label"
                htmlFor={`requires_detail_${index}`}
                style={{ fontSize: '0.85rem' }}
              >
                Pide detalle
              </label>
            </div>
          </div>

          <div className="col-md-1 text-end">
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => remove(index)}
              title="Eliminar opción"
              style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                border: 'none',
                borderRadius: '6px',
                padding: '3px 10px',
              }}
            >
              X
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={add}
        style={{ marginTop: '8px' }}
      >
        + Agregar opción
      </button>
    </>
  );
};

const PaymentEventForm = ({
  formData,
  onInputChange,
  onCommentOptionsChange,
  onSubmit,
  isEditing = false,
  onCancel,
  confirmedCount = 0,
  originalTitle = '',
  originalAmount = '',
}) => {

  const isFormValid = () => {
    return (
      formData.title?.trim() &&
      formData.amount !== '' &&
      parseFloat(formData.amount) >= 1
    );
  };

  const titleChanged =
    isEditing && formData.title?.trim() !== String(originalTitle).trim();
  const amountChanged =
    isEditing && String(formData.amount) !== String(originalAmount);

  return (
    <form onSubmit={onSubmit}>

      {isEditing && confirmedCount > 0 && amountChanged && (
        <div className="alert alert-warning" style={{ borderRadius: '6px' }}>
          Este evento ya tiene <strong>{confirmedCount}</strong> pago
          {confirmedCount === 1 ? '' : 's'} aprobado
          {confirmedCount === 1 ? '' : 's'} con el monto anterior (
          ${Number(originalAmount || 0).toLocaleString('es-AR')}). Cambiar el monto
          no modifica esos pagos: el valor nuevo aplica solo a los que se
          registren de acá en adelante.
        </div>
      )}

      {isEditing && titleChanged && (
        <div className="alert alert-info" style={{ borderRadius: '6px' }}>
          Al cambiar el título, los pagos ya registrados bajo{' '}
          <strong>{originalTitle}</strong> se reasignan automáticamente al
          título nuevo para no perder el vínculo.
        </div>
      )}

      <div className="mb-3 mt-3">
        <label htmlFor="title" className="form-label">
          Título del evento
        </label>
        <input
          type="text"
          className="form-control"
          id="title"
          name="title"
          placeholder="Ej: Viaje - Independiente vs Racing"
          maxLength={200}
          required
          onChange={onInputChange}
          value={formData.title}
        />
      </div>

      <div className="mb-3">
        <label htmlFor="amount" className="form-label">
          Monto ($)
        </label>
        <input
          type="number"
          className="form-control"
          id="amount"
          name="amount"
          placeholder="Ej: 3500"
          min={1}
          required
          onChange={onInputChange}
          value={formData.amount}
        />
        <small className="form-text text-muted">
          Ingresá el monto en pesos argentinos.
        </small>
      </div>

      <div className="mb-3">
        <label htmlFor="match_date" className="form-label">
          Fecha y hora del partido
        </label>
        <input
          type="datetime-local"
          className="form-control"
          id="match_date"
          name="match_date"
          onChange={onInputChange}
          value={formData.match_date}
        />
      </div>

      <div className="mb-3">
        <label htmlFor="sheet_tab" className="form-label">
          Pestaña de Google Sheet
        </label>
        <input
          type="text"
          className="form-control"
          id="sheet_tab"
          name="sheet_tab"
          placeholder="Ej: 14. Defensa"
          maxLength={100}
          onChange={onInputChange}
          value={formData.sheet_tab}
        />
        <small className="form-text text-muted">
          Nombre exacto de la pestaña en la planilla donde se registran los pagos.
        </small>
      </div>

      <hr style={{ margin: '24px 0 16px' }} />
      <h6 style={{ marginBottom: '12px', color: '#555' }}>Datos bancarios (transferencia)</h6>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="bank_cbu" className="form-label">CBU</label>
          <input
            type="text"
            className="form-control"
            id="bank_cbu"
            name="bank_cbu"
            placeholder="Ej: 0000003100012345678901"
            maxLength={30}
            onChange={onInputChange}
            value={formData.bank_cbu}
          />
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="bank_alias" className="form-label">Alias</label>
          <input
            type="text"
            className="form-control"
            id="bank_alias"
            name="bank_alias"
            placeholder="Ej: filial.roja.lp"
            maxLength={50}
            onChange={onInputChange}
            value={formData.bank_alias}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="bank_holder" className="form-label">Titular de la cuenta</label>
          <input
            type="text"
            className="form-control"
            id="bank_holder"
            name="bank_holder"
            placeholder="Ej: Juan Pérez"
            maxLength={120}
            onChange={onInputChange}
            value={formData.bank_holder}
          />
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="capacity" className="form-label">Capacidad máxima</label>
          <input
            type="number"
            className="form-control"
            id="capacity"
            name="capacity"
            placeholder="Ej: 50"
            min={1}
            onChange={onInputChange}
            value={formData.capacity}
          />
          <small className="form-text text-muted">
            Cantidad máxima de personas para este evento.
          </small>
        </div>
      </div>

      <hr style={{ margin: '24px 0 16px' }} />
      <h6 style={{ marginBottom: '4px', color: '#555' }}>Opciones de comentario</h6>
      <small className="form-text text-muted" style={{ display: 'block', marginBottom: '12px' }}>
        Lo que el socio elige al subir el comprobante. El color pinta esa celda
        en la planilla, y "pide detalle" abre un campo de texto libre en la app.
      </small>

      <CommentOptionsEditor
        options={formData.comment_options || []}
        onChange={onCommentOptionsChange}
      />

      <hr style={{ margin: '24px 0 16px' }} />

      <button
        type="submit"
        style={{
          backgroundColor: '#C73B33',
          borderColor: '#FF5733',
          color: '#FFF',
        }}
        className="btn"
        disabled={!isFormValid()}
      >
        {isEditing ? 'Guardar Cambios' : 'Crear Evento'}
      </button>

      {isEditing && (
        <button
          type="button"
          className="btn btn-secondary ms-2"
          onClick={onCancel}
        >
          Cancelar
        </button>
      )}

    </form>
  );
};

export default PaymentEventForm;
